import { FormEvent, useMemo, useState } from "react";
import { Button, Head } from "zudoku/components";
import { Alert, AlertDescription, AlertTitle } from "zudoku/ui/Alert";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "zudoku/ui/Card";
import { Input } from "zudoku/ui/Input";
import { Label } from "zudoku/ui/Label";
import { LogIn } from "zudoku/icons";
import { CustomAuthProvider } from "../../zudoku/auth/CustomAuthProvider";

const getRedirectTarget = (search: string): string => {
	const params = new URLSearchParams(search);
	return params.get("redirectTo") || "/settings/api-keys";
};

const LoginPage = () => {
	const redirectTo = useMemo(() => {
		const search = typeof window !== "undefined" ? window.location.search : "";
		return getRedirectTarget(search);
	}, []);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const authProvider = CustomAuthProvider.getInstance();

	const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!authProvider) {
			setError("Authentication is not initialized yet. Please try again.");
			return;
		}

		setError(null);
		setIsSubmitting(true);

		try {
			await authProvider.signInWithPassword(email, password, redirectTo);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Unable to sign in with password.";
			setError(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMicrosoftLogin = async () => {
		if (!authProvider) {
			setError("Authentication is not initialized yet. Please try again.");
			return;
		}

		setError(null);
		setIsSubmitting(true);

		try {
			await authProvider.signInWithMicrosoft({
				redirectTo,
			});
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: "Unable to start Microsoft sign in.";
			setError(message);
			setIsSubmitting(false);
		}
	};

	return (
		<section className="flex min-h-[calc(100vh-120px)] flex-col items-center justify-center px-4 py-12">
			<Head>
				<title>Login</title>
			</Head>

			<Card className="w-full max-w-lg shadow-lg">
				<CardHeader className="space-y-2 text-center">
					<CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
					<CardDescription>
						Access your Beam developer portal account.
					</CardDescription>
				</CardHeader>

				<CardContent className="space-y-4">
					{error ? (
						<Alert variant="destructive">
							<AlertTitle>Login failed</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					) : null}

					<form className="space-y-4" onSubmit={handlePasswordSubmit}>
						<div className="space-y-2 text-left">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								autoComplete="email"
								required
								placeholder="you@example.com"
								value={email}
								onChange={(event) => setEmail(event.target.value)}
							/>
						</div>

						<div className="space-y-2 text-left">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								autoComplete="current-password"
								required
								placeholder="Enter your password"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
							/>
						</div>

						<Button
							type="submit"
							className="w-full"
							disabled={isSubmitting || !email || !password}
						>
							{isSubmitting ? "Signing in..." : "Sign in with email"}
						</Button>
					</form>

					<div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
						<span className="h-px flex-1 bg-border" />
						or
						<span className="h-px flex-1 bg-border" />
					</div>

					<Button
						variant="outline"
						className="flex w-full items-center justify-center gap-2"
						onClick={handleMicrosoftLogin}
						disabled={isSubmitting}
					>
						<LogIn size={18} />
						<span>Sign in with Microsoft</span>
					</Button>
				</CardContent>

				<CardFooter className="flex flex-col gap-2 text-xs text-muted-foreground">
					<p>
						Having trouble signing in?{" "}
						<a
							className="text-primary underline underline-offset-4 hover:text-primary/80"
							href="https://beamconnectivity.com/contact/"
							target="_blank"
							rel="noreferrer"
						>
							Contact us
						</a>{" "}
						for help.
					</p>
				</CardFooter>
			</Card>
		</section>
	);
};

export default LoginPage;
